import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage, getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Ranges where both
 * endpoints are the same are collapsed (e.g. "2 - 2" → "2 Bedroom").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse ranges where every part is identical (e.g. "2 - 2" → "2").
  const deduped =
    formatted.length > 1 && formatted.every((p) => p === formatted[0])
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultValue:
        'https://media.multihub.io/property/6767906449167749/01a0dad4-397b-7570-b69b-174e841b0493.mp4',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bg = useSlotGroup('hero.bg_image', bgDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  const showVideo = !!bg.video;
  // Derive the poster from the bound video so it stays correct when the
  // operator overrides the slot to a different video. Falls back to the image.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster = (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  // Respect prefers-reduced-motion: drop the autoplay attribute (pause() alone
  // loses the race if the video hasn't buffered yet) and pause anything already
  // playing. Leaves the poster frame showing.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="relative w-full h-[75vh] md:h-screen min-h-[400px] md:min-h-[600px] max-h-[1000px] overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={bgDef}>
        <div className="absolute inset-0">
          {showVideo ? (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src={bg.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
          ) : bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt=""
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-1 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="h2"
          className="hero-entrance hero-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl mb-10 leading-relaxed opacity-90 drop-shadow-md"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
            <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.2s; }
        .hero-entrance-2 { animation-delay: 0.45s; }
        .hero-entrance-3 { animation-delay: 0.7s; }
        .hero-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .hero-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          left: 100%;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': bgDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
